import { ChevronRight } from 'lucide-react';
import { MarkdownPreview } from '../ui/MarkdownPreview';
import {
  buildRoleChatTranscript,
  matchTaskToRoleCode,
  type RoleChatCursorEvent,
} from './role-chat-rendering.utils';

const muted = 'text-xs text-gray-500 dark:text-gray-400';
const summaryClass = `${muted} flex cursor-pointer list-none items-center gap-1 select-none [&::-webkit-details-marker]:hidden`;
const resultBox = 'mt-1 max-h-64 overflow-auto whitespace-pre-wrap rounded-md border border-gray-700 px-3 py-2 font-sans text-xs text-gray-300';

const SHELL_TOOLS = new Set(['shell', 'run_terminal_cmd']);
const FILE_LABELS: Record<string, string> = {
  read: 'Read',
  read_file: 'Read',
  file_search: 'Search',
  write: 'Write',
  edit: 'Edit',
  edit_file: 'Edit',
  delete: 'Delete',
  grep: 'Grep',
  grep_search: 'Grep',
  glob: 'Glob',
};

function thoughtLabel(seconds: number): string {
  if (seconds <= 0) return 'Thought';
  return seconds === 1 ? 'Thought for 1 second' : `Thought for ${seconds} seconds`;
}

function prettyOutput(output: string): string {
  const text = output.trim();
  if (!text.startsWith('{') && !text.startsWith('[')) return output;
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return output;
  }
}

function ChevronSummary({ label }: { label: string }) {
  return (
    <summary className={summaryClass}>
      <span>{label}</span>
      <ChevronRight className="h-3 w-3 shrink-0 transition-transform group-open:rotate-90" />
    </summary>
  );
}

export function RoleChatTranscript({
  content,
  events,
  roleCodes,
  onOpenRole,
}: {
  content: string;
  events?: RoleChatCursorEvent[];
  roleCodes: string[];
  onOpenRole?: (roleCode: string) => void;
}) {
  const blocks = buildRoleChatTranscript(events, content);
  if (blocks.length === 0) return null;
  return (
    <div className="space-y-2">
      {blocks.map((block, index) => {
        if (block.kind === 'text') {
          return (
            <MarkdownPreview
              key={`text-${index}`}
              content={block.text}
              variant="chat"
              className="text-sm text-gray-900 dark:text-gray-100 [&_.prose]:text-sm"
            />
          );
        }
        if (block.kind === 'thinking') {
          return (
            <details key={`thinking-${index}`} className="group">
              <ChevronSummary label={thoughtLabel(block.seconds)} />
              {block.text ? <p className={`mt-1 whitespace-pre-wrap pl-3 ${muted}`}>{block.text}</p> : null}
            </details>
          );
        }
        if (block.kind === 'tool') {
          const isShell = SHELL_TOOLS.has(block.name);
          const verb = block.status === 'running' ? 'Using' : 'Used';
          const fileLabel = FILE_LABELS[block.name];
          const shortDetail = block.detail.split('\n')[0]?.trim() || '';
          const summaryDetail = shortDetail.length > 120 ? `${shortDetail.slice(0, 120)}…` : shortDetail;
          const label = isShell
            ? `$ ${summaryDetail || block.name}`
            : fileLabel
              ? `${fileLabel}${summaryDetail ? ` ${summaryDetail}` : ''}`
              : block.provider
                ? `${verb} ${block.name} with ${block.provider}`
                : `${verb} ${block.name}`;
          const outputBody = block.output ? prettyOutput(block.output) : '';
          if (!block.detail && !outputBody) return <p key={`tool-${index}`} className={muted}>{label}</p>;
          return (
            <details key={`tool-${index}`} className="group">
              <ChevronSummary label={label} />
              {block.detail ? <pre className={resultBox}>{block.detail}</pre> : null}
              {outputBody ? <pre className={resultBox}>{outputBody}</pre> : null}
            </details>
          );
        }
        if (block.kind === 'task') {
          const roleCode = matchTaskToRoleCode(block.title, roleCodes);
          const open = roleCode && onOpenRole
            ? () => onOpenRole(roleCode)
            : undefined;
          return (
            <button
              key={`task-${index}`}
              type="button"
              className={`${muted} flex items-center gap-1 text-left hover:text-gray-700 dark:hover:text-gray-200 disabled:cursor-default`}
              disabled={!open}
              onClick={open}
            >
              <ChevronRight className="h-3 w-3 shrink-0" />
              <span>{block.title}</span>
            </button>
          );
        }
        return (
          <p key={`status-${index}`} className={muted}>{block.text}</p>
        );
      })}
    </div>
  );
}
